import styled from '@emotion/styled';
import { CSSObject } from '@emotion/react';
import { Flex, Icon, Image, Text } from '../../../styles';
import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { Size } from '../../../styles/Icon';
import theme, { ColorKey } from '../../../theme';
import { __t } from '../../../i18n';
import { IconAngleLeft } from '../../../icons';
import { ToggleButton } from '../../../ToggleBtn';
import { PreviewThumbsProps } from '../PlayerModels';
import { layersZIndex } from '../PlayerServices';

/**
 * The chrome's compact treatment: applied on narrow viewports as before, and additionally
 * whenever the player is embedded in the shorts panel. That panel is always narrow regardless
 * of window size, so a viewport media query alone never fires for it — the `.short-player`
 * class (set by the players' `isShort` prop) stands in for "narrow" there.
 *
 * Emitting both from one call keeps the two in step; the full-page player is unaffected.
 */
export const compactChrome = (styles: CSSObject, shortsOnly: CSSObject = {}) => ({
  [theme.breakpoints.down('md')]: styles,
  // `shortsOnly` is for what the shorts panel needs beyond being narrow — above all `fixed`
  // positioning, which anchors to the window and so lands outside the player box there.
  '.short-player &': { ...styles, ...shortsOnly },
});

// export const PanelWrapper = styled(Box)(({ theme }) => {
//   return {
//     position: 'absolute',
//     bottom: '3.5vw',
//     height: 'auto',
//     right: '0',
//     background: theme.palette.background[900][0],
//     borderRadius: '2.92vw',
//     transition: 'opacity 0.3s ease-in-out 0s, transform 0.3s ease-in-out 0s',
//     zIndex: '20',
//     padding: '5vw 3.3vw',
//     width: '12.4vw',
//   };
// });

export const RelativeWrapper = styled.div((props: React.ComponentProps<any>) => ({
  position: 'relative',
  display: 'flex',
  marginRight: props.mr ? `${props.mr}rem` : 0,
}));

export const ProgressRelativeWrapper = styled.div(() => ({
  position: 'relative',
}));

export const AbsoluteCenter = styled.div({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  margin: '0 auto auto 0',
});

// export const FlexCenter = styled.div<{ flexDirection?: 'row' | 'column', padding?: string }>(({ flexDirection = 'row', padding }) => (
//   {
//     display: 'flex',
//     justifyContent: 'center',
//     alignItems: 'center',
//     flexDirection,
//     padding,
//   }));

// export const ButtonReset = styled.button(({ theme }) => {
//   return {
//     border: 'none',
//     fontFamily: 'inherit',
//     fontWeight: 400,
//     backgroundColor: 'transparent',
//     borderRadius: '0.1vw',
//     color: theme.palette.background[900][0],
//     cursor: 'pointer',
//     whiteSpace: 'nowrap',
//     direction: 'rtl',
//     borderStyle: 'solid',
//     borderColor: 'transparent',
//     '&:hover': {
//       borderColor: theme.palette.background[900][0],
//     },
//   };
// });

export const CustomOption = styled.div<{ selected: boolean }>(({ theme, selected }) => {
  return {
    border: 'none',
    fontFamily: 'inherit',
    fontWeight: 400,
    backgroundColor: 'transparent',
    color: theme.palette.text[900][1],
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    direction: 'rtl',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'right',
    width: '100%',
    padding: '0.75rem 1rem',
    alignItems: 'center',
    fontSize: '0.875rem',
    justifyContent: 'space-between',
    ':hover': {
      backgroundColor: theme.palette.background[500][0],
    },
    '> li ': {
      paddingRight: '0',
      direction: 'rtl',
    },
    ...compactChrome({ padding: '0.5rem 1rem' }),
    '&:hover': {
      borderColor: 'transparent',
    },
    '&::before': {
      verticalAlign: 'middle',
      content: '""',
      display: 'inline-block',
      width: '0.75rem',
      height: '0.75rem',
      marginLeft: '1vw',
      borderRadius: '50%',
      borderStyle: 'solid',
      borderColor: theme.palette.text[900][1],
      borderWidth: '1px',
      backgroundColor: selected ? theme.palette.text[900][1] : 'transparent',
      transition: 'background-color .3s ease',
    },
  };
});

export const CustomOptionWrapper = styled.button(() => {
  return {
    display: 'flex',
    cursor: 'pointer',
    width: '100%',
    padding: 0,
  };
});

// export const IconWrapper = styled.div<{ cursor?: string }>(({ cursor = 'pointer' }) => ({
//   padding: '0 0.5vw',
//   cursor,
//   '@media (max-width: 1000px) and (min-width: 701px)': {
//     '& svg': {
//       width: '1.75rem',
//       height: '1.75rem',
//     },
//   },
//   '@media (max-width: 700px)': {
//     '& svg': {
//       width: '1.75rem',
//       height: '1.75rem',
//     },
//   },
// }));

export const ControlBarActionBtn = styled.button(() => ({
  padding: '0',
}));

export const ActionBarBtn = styled(ControlBarActionBtn)(({ theme }) => {
  return {
    background: theme.palette.foreground[700][0],
    color: theme.palette.background[800][0],
    direction: 'ltr',
    fontWeight: 500,
    borderRadius: '0.5rem',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.foreground[900][0],
    },
    '& svg': {
      height: '1rem',
      width: '1rem',
      marginLeft: '0.5rem',
      fill: theme.palette.background[800][0],
    },
  };
});

export const ActionBarBtnWrapper = styled.div<{ marginLeft?: string, marginRight?: string }>(({ marginLeft, marginRight }) => {
  return {
    marginRight,
    marginLeft,
  };
});

// Player Custom/Vast components
export const ControlBarWrapper = styled.div(() => {
  return {
    position: 'absolute',
    width: '100vw',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '1.5rem',
    zIndex: 2,
    ...compactChrome({ padding: '1rem' }),
    // Embedded in the shorts panel the player is narrower than the viewport, so 100vw spills
    // past it (and clips the progress bar). Fill the wrapper instead — see .short-player.
    '.short-player &': {
      width: '100%',
      padding: '1rem',
    },
  };
});

export const CustomControlWrapper = styled.div<{ show: boolean }>(({ show }) => {
  return {
    position: 'absolute',
    top: 0,
    height: '100%',
    width: '100vw',
    display: show ? undefined : 'none',
    '.short-player &': {
      width: '100%',
    },
  };
});

interface PlayerControlButtonProps extends React.ComponentProps<any> {
  icon: React.ReactNode;
  size?: Size,
  active?: boolean,
  disabled?: boolean
}

export const PlayerControlButton: FunctionComponent<PlayerControlButtonProps> = ({
  icon,
  disabled,
  size = 'medium',
  active = false,
  ...rest
}) => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    handleMobileBreakpoint();
    window.addEventListener('resize', handleMobileBreakpoint);
    return () => {
      window.removeEventListener('resize', handleMobileBreakpoint);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };
  return (
    <RelativeWrapper {...rest}>
      <ControlButton disabled={disabled}>
        <PlayerControlButtonIcon
          disabled={disabled}
          size={size}
          color={active ? 'primary' : 'foreground'}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          isMobile={isMobile}
        >
          {icon}
        </PlayerControlButtonIcon>
      </ControlButton>
    </RelativeWrapper>
  );
};

export const ControlButton = styled.button(() => ({
  padding: '0',
}));

export const PlayerControlButtonIcon = styled(Icon)<{ disabled?: boolean, isMobile?: boolean, color: ColorKey }>(({
  disabled,
  isMobile,
  color,
}) => {
  return {
    '&:hover, &:active, &:focus, &:focus-visible': {
      color: disabled ? 'inherit' : isMobile ? theme.palette[color][900][0] : theme.palette.primary[900][0],
    },
  };
});

interface SettingPanelItemProps {
  icon: React.ReactNode;
  onClick: (e: MouseEvent) => void;
  label: string,
  value?: string,
  isAtLiveEdge?: boolean,
}

export const SettingPanelMultiSelectItem: FunctionComponent<SettingPanelItemProps> =
  ({ icon, onClick, label, value, isAtLiveEdge = false }) => {
    return <PanelMultiSelectItemWrapper cursor={isAtLiveEdge ? 'default' : 'pointer'} opacity={isAtLiveEdge ? '0.4' : '1'} onClick={onClick}>
      <PanelItemIconLabel icon={icon} label={label} />
      <Flex align="center">
        <Text variant="body3" ml={1} pt={value === 'HQ' ? '5px' : 0}>{value}</Text>
        <Icon>
          <IconAngleLeft />
        </Icon>
      </Flex>
    </PanelMultiSelectItemWrapper>;
  };

export const SettingPanelItem: FunctionComponent<SettingPanelItemProps &
  { checked: boolean }> = ({ icon, onClick, label, checked }) => {
    return <PanelMultiSelectItemWrapper onClick={onClick} cursor="pointer">
      <PanelItemIconLabel icon={icon} label={label} />
      <ToggleButton handleCheck={onClick} name={label} defaultChecked={checked} />
    </PanelMultiSelectItemWrapper>;
  };

export const PanelItemIconLabel = ({ icon, label }) => {
  return <Flex align="center" justify="flex-start">
    <Icon m={0}>
      {icon}
    </Icon>
    <Text variant="body2" align="right" mr={1}>{__t(label)}</Text>
  </Flex>;
};

export const PanelMultiSelectItemWrapper = styled.div<{ cursor?: string, opacity?: string; notHover?:boolean }>(({ cursor, opacity = '1', notHover }) => {
  return {
    display: 'flex',
    direction: 'rtl',
    width: '100%',
    padding: '0.75rem 1rem',
    alignItems: 'center',
    fontSize: '0.875rem',
    opacity,
    cursor,
    ': hover': {
      background: notHover ? 'inherit' : theme.palette.background[500][0],
    },
    justifyContent: 'space-between',
    '> li ': {
      paddingRight: '0',
      direction: 'rtl',
    },
    ...compactChrome({ padding: '0.5rem 1rem' }),
  };
});

export const ActionBarContainer = styled.div(() => ({
  position: 'absolute',
  right: 0,
  bottom: '10.25rem',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  paddingRight: '1.5rem',
  zIndex: 2,
  flexDirection: 'row-reverse',
  ...compactChrome({ bottom: '6.5rem' }),
}));

export const ActionBarFlexWrapper = styled(Flex)({
  minWidth: '23vw',
  marginLeft: 'auto',
  '.short-player &': {
    minWidth: 'calc(23 * var(--short-player-vw))',
  },
});

export const HoverTimeDetector = styled(AbsoluteCenter)<{ left: number }>(({ theme, left }) => {
  return {
    zIndex: 21,
    height: '1.5rem',
    left: left < 2 ? '1vw' : left < 99 ? `${left}%` : '99%',
    minWidth: '4rem',
    bottom: '1rem',
    top: 'auto',
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background[900][0],
    fontSize: '0.75rem',
    direction: 'ltr',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.6rem',
    },
  };
});

export const ProgressTrack = styled.div<{ playingAd?: boolean }>(({ theme, playingAd = false }) => {
  return {
    width: '100%',
    position: 'relative',
    backgroundColor: theme.palette.foreground[100][0],
    borderRadius: '0.5rem',
    opacity: 1,
    height: '0.25rem',
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'height 0.1s ease-out',
    ':hover': {
      height: playingAd ? '0.25rem' : '0.75rem',
    },
    ['@media (max-width: 1250px)']: {
      height: '0.5rem',
      ':hover': {
        height: playingAd ? '0.5rem' : '0.75rem',
      },
    },
    '::webkitUserSelect': 'none',
    '::msUserSelect': 'none',
    userSelect: 'none',
  };
});

export const ProgressInputRange = styled.input<{ playingAd: boolean }>(({ playingAd }) => ({
  borderRadius: '0.5rem',
  margin: 0,
  padding: 0,
  width: '100%',
  height: '1rem',
  display: 'block',
  position: 'absolute',
  overflow: 'hidden',
  top: 0,
  left: 0,
  backgroundColor: 'transparent',
  // outline: 'none',
  border: 'none',
  direction: 'ltr',
  WebkitAppearance: 'none',
  // appearance: 'none',
  zIndex: 5,
  cursor: playingAd ? '' : 'pointer',
  '::webkitUserSelect': 'none',
  '::msUserSelect': 'none',
  userSelect: 'none',
  '::-webkit-slider-thumb': {
    width: 0,
    WebkitAppearance: 'none',
  },
  '::-moz-range-track': {
    border: 'none',
  },
  '::-moz-range-thumb': {
    width: 0,
  },
  '::-ms-track': {
    background: 'transparent',
    borderColor: 'transparent',
    color: 'transparent',
  },
  '::-ms-thumb': {
    width: 0,
    border: 'none',
    direction: 'ltr',
    WebkitAppearance: 'none',
    appearance: 'none',
    zIndex: 5,
    cursor: playingAd ? '' : 'pointer',
    '::-webkit-slider-thumb': {
      width: 0,
      WebkitAppearance: 'none',
    },
    '::-moz-range-track': {
      border: 'none',
    },
    '::-moz-range-thumb': {
      width: 0,
    },
    '::-ms-track': {
      background: 'transparent',
      borderColor: 'transparent',
      color: 'transparent',
    },
    '::-ms-thumb': {
      width: 0,
      border: 'none',
      color: 'transparent',
    },
  },
}));

export const ProgressBufferRange = styled.div<{ bufferPercent: number }>(({ theme, bufferPercent }) => {
  return {
    borderRadius: '0.5rem',
    zIndex: 10,
    position: 'absolute',
    height: '100%',
    width: `${bufferPercent}%`,
    maxWidth: '100%',
    top: 0,
    left: 0,
    pointerEvents: 'none',
    backgroundColor: theme.palette.foreground[600][0],
    opacity: 0.8,
  };
});
export const ProgressHoverDetector = styled(AbsoluteCenter)<{ left: number, playingAd: boolean }>(({ theme, left, playingAd }) => {
  return {
    borderRadius: '50%',
    zIndex: 21,
    bottom: 0,
    borderStyle: 'solid',
    borderColor: playingAd ? theme.palette.success[900][0] : theme.palette.primary[800][0],
    height: '1rem',
    left: `${left}%`,
    minWidth: '1rem',
    pointerEvents: 'none',
    backgroundColor: playingAd ? theme.palette.success[900][0] : theme.palette.primary[800][0],
  };
});

export const VolumeRangeWrapper = styled.div(({ theme }) => ({
  zIndex: 2,
  display: 'inline-flex',
  width: '6em',
  height: '3px',
  position: 'relative',
  padding: 0,
  margin: 0,
  borderRadius: '1px',
  backgroundColor: theme.palette.foreground[100][0],
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
}));

export const VolumeRange = styled.input(() => ({
  zIndex: 2,
  position: 'absolute',
  direction: 'ltr',
  margin: 0,
  padding: 0,
  width: '100%',
  height: '6px',
  display: 'block',
  top: '-1px',
  overflow: 'hidden',
  left: 0,
  backgroundColor: 'transparent',
  outline: 'none',
  border: 'none',
  WebkitAppearance: 'none',
  appearance: 'none',
  cursor: 'pointer',
  '::-webkit-slider-thumb': {
    width: 0,
    WebkitAppearance: 'none',
  },
  '::-moz-range-track': {
    border: 'none',
  },
  '::-moz-range-thumb': {
    width: 0,
  },
  '::-ms-track': {
    background: 'transparent',
    borderColor: 'transparent',
    color: 'transparent',
  },
  '::-ms-thumb': {
    width: 0,
    border: 'none',
    color: 'transparent',
  },
}));

export const FillVolumeUpRange = styled.div<{ volume: number }>(({ theme, volume }) => {
  return {
    width: `${volume * 100}%`,
    height: '3px',
    position: 'absolute',
    backgroundColor: theme.palette.primary[800][0],
    borderRadius: '1px',
    top: 0,
    left: 0,
  };
});

export const VolumeHoverDetector = styled(AbsoluteCenter)<{ left: number }>(({ theme, left }) => {
  return {
    height: '0.6rem',
    minWidth: '0.6rem',
    borderRadius: '50%',
    zIndex: 21,
    borderStyle: 'solid',
    backgroundColor: theme.palette.primary[800][0],
    borderColor: theme.palette.primary[800][0],
    pointerEvents: 'none',
    left: `${left * 100}%`,
  };
});

export const VolumeSectionContainer = styled.div(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const CompTitle = styled.button(() => {
  return {
    display: 'flex',
    justifyItems: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    width: '100%',
  };
});

export const SeasonItem = styled.button(({ theme }) => {
  return {
    backgroundColor: theme.palette.background[700][0],
    borderRadius: '0.5rem',
    padding: '1rem 1rem',
    width: '16rem',
    marginBottom: '0.5rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background[600][0],
    },
  };
});

export const SeasonTitleWrapper = styled.button(({ theme }) => {
  return {
    backgroundColor: theme.palette.background[700][0],
    borderRadius: '0.5rem',
    padding: '0.5rem 1rem',
    width: '16rem',
    cursor: 'pointer',
    marginBottom: '1.25rem',
    '&:hover': {
      backgroundColor: theme.palette.background[600][0],
    },
  };
});

export const SeriesImage = styled(Image)(() => {
  return {
    width: '6.3rem',
    height: '3.5rem',
    borderRadius: '0.25rem',
    ...compactChrome({ height: '2.6rem', width: '4.5rem' }),
  };
});

export const SeasonTitleTextWrapper = styled.div(({ theme }) => {
  return {
    backgroundColor: theme.palette.background[700][0],
    borderRadius: '0.5rem',
    padding: '0.5rem 1rem',
    width: '16rem',
    marginBottom: '1.25rem',
    textAlign: 'right',
    ...compactChrome({ marginBottom: '1rem' }),
  };
});

export const EpisodeItem = styled.button<{ isActive: boolean }>(({ theme, isActive }) => {
  return {
    backgroundColor: isActive ? theme.palette.background[600][0] : theme.palette.background[700][0],
    borderRadius: '0.5rem',
    padding: '0.5rem',
    width: '16rem',
    marginBottom: '0.5rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background[600][0],
    },
  };
});

export const SeriesImageWrapper = styled.div(() => {
  return {
    position: 'relative',
    marginLeft: '1rem',
    borderRadius: '0.25rem',
  };
});

export const SeriesIconWrapper = styled.div(({ theme }) => {
  return {
    position: 'absolute',
    zIndex: 20,
    backgroundColor: theme.palette.primary[800][0],
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '100%',
    top: '50%',
    transform: 'translateY(-50%) translateX(-50%)',
    left: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
});

export const SeriesBadge = styled.div(({ theme }) => {
  return {
    zIndex: 20,
    backgroundColor: theme.palette.warning[300][0],
    width: '3.5rem',
    height: '1.5rem',
    borderRadius: '60px',
  };
});

export const ScrollableList = styled.div(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    position: 'relative',
    maxHeight: '23rem',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '&::-webkit-scrollbar-thumb': {
      display: 'none',
    },
    ...compactChrome({ maxHeight: '11rem' }),
  };
});

export const ErrorBox = styled(Flex)(({ theme }) => ({
  textAlign: 'center',
  padding: '1.5rem',
  direction: 'ltr',
  '& button': {
    fontSize: '1rem',
    height: '2.5rem',
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.background[800][0],
    },
  },
  '@media (max-width: 900px)': {
    padding: 0,
  },
}));

export const BadgeWrapper = styled(Flex)<{ isBehind: boolean }>(({ isBehind }) => {
  return {
    cursor: isBehind ? 'pointer' : 'inherit',
  };
});

export const ButtonIcon = styled(ControlBarActionBtn)(({ theme }) => ({
  cursor: 'pointer',
  width: '2.5rem',
  height: '2.5rem',
  padding: '0',
  backgroundColor: theme.palette.text[800],
  borderRadius: '0.5rem',
  '& svg': {
    height: '1rem',
    width: '1rem',
  },
}));

export const NextActionBarBtn = styled(ActionBarBtn)({
  minWidth: '11.7rem',
  marginLeft: '1rem',
});

export const Timer = styled.div({
  width: '2rem',
  color: theme.palette.background[800][0],
});

export const BigBtnFloatingContainer = styled(AbsoluteCenter)(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  transition: 'opacity .3s ease',
  direction: 'rtl',
  zIndex: 2,
}));

export const BigPlayBtnFloatingButton = styled.button<{ isPlay: boolean }>(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.rgba(theme.palette.background[800][0], 0.4),
  cursor: 'pointer',
  minWidth: '6.7vw',
  height: '6.7vw',
  margin: '0 3vw',
  borderRadius: '50%',
  borderStyle: 'solid',
  borderColor: 'transparent',
  ':hover': {
    backgroundColor: theme.rgba(theme.palette.background[800][0], 0.6),
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  '& svg': {
    width: '3vw',
    height: '3vw',
  },
  '@media (max-width: 1000px) and (min-width: 701px)': {
    minWidth: '10vw',
    height: '10vw',
    '& svg': {
      width: '5vw',
      height: '5vw',
    },
  },
  '@media (max-width: 700px)': {
    minWidth: '15vw',
    height: '15vw',
    '& svg': {
      width: '8vw',
      height: '8vw',
    },
  },
  // Embedded in the shorts panel: same proportions the narrow-viewport branch above gives,
  // but measured against the player box rather than the window.
  '.short-player &': {
    minWidth: 'calc(15 * var(--short-player-vw))',
    height: 'calc(15 * var(--short-player-vw))',
    margin: '0 calc(3 * var(--short-player-vw))',
    '& svg': {
      width: 'calc(8 * var(--short-player-vw))',
      height: 'calc(8 * var(--short-player-vw))',
    },
  },
}));

export const BigBtnFloatingButton = styled.button<{ isPlay: boolean }>(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.rgba(theme.palette.background[800][0], 0.4),
  cursor: 'pointer',
  minWidth: '5vw',
  height: '5vw',
  margin: '0 3vw',
  borderRadius: '50%',
  borderStyle: 'solid',
  borderColor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  ':hover': {
    backgroundColor: theme.rgba(theme.palette.background[800][0], 0.6),
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  '& svg': {
    width: '2.8vw',
    height: '2.8vw',
  },
  '@media (max-width: 1000px) and (min-width: 701px)': {
    minWidth: '8vw',
    height: '8vw',
    '& svg': {
      width: '4vw',
      height: '4vw',
    },
  },
  '@media (max-width: 700px)': {
    minWidth: '13vw',
    height: '13vw',
    '& svg': {
      width: '6vw',
      height: '6vw',
    },
  },
  '.short-player &': {
    minWidth: 'calc(13 * var(--short-player-vw))',
    height: 'calc(13 * var(--short-player-vw))',
    margin: '0 calc(3 * var(--short-player-vw))',
    '& svg': {
      width: 'calc(6 * var(--short-player-vw))',
      height: 'calc(6 * var(--short-player-vw))',
    },
  },
}));
export const CustomControlButtonList = styled.div(() => {
  return {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    direction: 'ltr',
    marginTop: '1.5rem',
  };
});

export const ScreenPlayContainer = styled.div(() => {
  return {
    width: '100vw',
    height: '100%',
    position: 'fixed',
    top: '0',
    zIndex: layersZIndex.toolbar.screen,
    '::webkitUserSelect': 'none',
    '::msUserSelect': 'none',
    userSelect: 'none',
    // `fixed` + 100vw covers the whole viewport, which swallows everything around the shorts
    // panel; anchor the click/play surface to the player box instead.
    '.short-player &': {
      position: 'absolute',
      inset: 0,
      width: '100%',
    },
  };
});

// The top right corner is taken by the back button and the watermark, so the notice sits in
// the free corner opposite them rather than over the middle of the picture.
export const UnmuteNoticeWrapper = styled.div(({ theme }) => ({
  position: 'fixed',
  top: '1.5rem',
  left: '1.5rem',
  // Size to the content: a shrink-to-fit box against the left edge still has the whole width
  // to grow into, and the max width keeps a gutter before the corner the back button holds.
  width: 'max-content',
  maxWidth: 'calc(100% - 3rem)',
  zIndex: layersZIndex.toolbar.unmuteNotice,
  // Scoped here rather than on ActionBarBtn, which SkipIntro and WatchFromStart share: keeps
  // the icon beside the label on the narrow screens where it wraps at that max width.
  '& button': {
    display: 'flex',
    alignItems: 'center',
  },
  [theme.breakpoints.down('md')]: {
    top: '1rem',
    left: '1rem',
    maxWidth: 'calc(100% - 2rem)',
  },
  // In the shorts panel the player is a box inside the page rather than the viewport, so
  // `fixed` places the notice against the window - beside the player, over the desktop info
  // panel. Anchor it to the player instead; `.short-player` is `inset: 0`, so it is the box.
  '.short-player &': {
    position: 'absolute',
    top: '1rem',
    left: '1rem',
  },
}));

// Shares the layer of ScreenPlayContainer but is rendered after it, so while the notice is up a
// click on the video turns the sound on instead of toggling playback. Higher layers - control
// bar, big buttons, the notice itself - stay clickable.
export const UnmuteScreenDetector = styled.div(() => ({
  width: '100vw',
  height: '100%',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: layersZIndex.toolbar.screen,
  // Same reason as ScreenPlayContainer, whose layer this shares: `fixed` + 100vw covers the
  // whole viewport, so in the shorts panel a click meant for the episode list beside the player
  // would land here and unmute instead. Keep the surface on the player box.
  '.short-player &': {
    position: 'absolute',
    inset: 0,
    width: '100%',
  },
}));

export const ThumbWrapper = styled.div<Pick<PreviewThumbsProps, 'show'>>(({ theme, show }) => ({
  backgroundColor: theme.rgba(theme.palette.background[800][0], 0.5),
  pointerEvents: 'none',
  opacity: show ? 1 : 0,
  transform: `translateY(${show ? 0 : '20%'}) scale(${show ? 1 : 0.85})`,
  transformOrigin: 'bottom center',
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  position: 'absolute',
  bottom: '2rem',
  width: '25%',
  maxWidth: 256,
  maxHeight: 144,
  borderRadius: 8,
}));

export const ThumbFrame = styled.div<{
  ratio: number,
  src: string,
  cols: number,
  rows: number,
  offsetX: number,
  offsetY: number
}>(({ ratio, src, cols, rows, offsetX, offsetY }) => {
  return {
    paddingTop: `${ratio * 100}%`,
    backgroundImage: `url(${src})`,
    backgroundSize: `${cols * 100}% ${rows * 100}%`,
    backgroundPosition: `left ${offsetX}% top ${offsetY}%`,
    backgroundRepeat: 'no-repeat',
    overflow: 'hidden',
    width: '100%',
    borderRadius: 8,
  };
});

export const ThumbCurrentTime = styled.div(({ theme }) => ({
  width: '100%',
  fontSize: '0.75rem',
  position: 'absolute',
  background: theme.palette.background[900][0],
  borderBottomRightRadius: 8,
  borderBottomLeftRadius: 8,
  textAlign: 'center',
  padding: '0.16rem 0',
  zIndex: 2,
  bottom: 0,
  right: 0,
}));

export const CustomReminder = styled(Text)(() => {
  return {
    position: 'absolute',
    right: '1.5rem',
    bottom: '6rem',
    direction: 'ltr',
    zIndex: 2,
    ...compactChrome({ bottom: '5rem' }),
  };
});

export const LinkButton = styled.button(({ theme }) => ({
  position: 'fixed',
  right: '1.5rem',
  top: '1rem',
  msFlexDirection: 'column',
  // On the control layer, like the control bar: the back button has to stay clickable while
  // the click zone of the unmute notice covers the video.
  zIndex: layersZIndex.toolbar.bar,
  transition: 'transform 0.3s ease-in-out, opacity .3s ease',
  flexDirection: 'row',
  backgroundColor: theme.palette.background[400][0],
  borderRadius: '0.5rem',
  cursor: 'pointer',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderWidth: 0,
  '&:hover': {
    backgroundColor: theme.palette.background[300][0],
  },
  textAlign: 'right',
  padding: '0.5rem',
  direction: 'rtl',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '4rem',
  ...compactChrome({ padding: '0.25rem 0.5rem' }, { position: 'absolute' }),
}));

export const EllipsisWrapper = styled(Text)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

// export const LinkButtonRightHand = styled.div(({ theme }) => ({
//   display: 'inline-flex',
//   minWidth: '1rem',
//   margin: '0.5rem 0 auto 0',
//   '& svg': {
//     width: '1rem',
//     height: '1rem',
//   },
//   [theme.breakpoints.down('md')]: {
//     margin: '0.6rem 0 auto 0',
//     '& svg': {
//       width: '0.4rem',
//     },
//   },
// }));

// export const LinkButtonLeftHand = styled.div(({ theme }) => ({
//   display: 'flex',
//   msFlexDirection: 'column',
//   padding: '0 0.5rem 0 0',
//   margin: '0',
//   flexDirection: 'column',
//   '& p': {
//     fontSize: '1rem',
//     lineHeight: '2rem',
//     fontWeight: 500,
//     whiteSpace: 'nowrap',
//   },
//   '& small': {
//     fontSize: '0.8rem',
//     fontWeight: 400,
//   },
//   [theme.breakpoints.down('md')]: {
//     padding: '0',
//     '& p': {
//       fontSize: '0.8rem',
//       lineHeight: 2,
//     },
//     '& small': {
//       fontSize: '0.7rem',
//     },
//   },
// }));

export const CarrierMessageContainer = styled.div(({ theme }) => ({
  position: 'fixed',
  alignItems: 'center',
  right: '0',
  top: '5rem',
  direction: 'rtl',
  padding: '0.75rem 1.5rem 0.75rem 1rem',
  marginTop: '0.5rem',
  fontSize: '0.75rem',
  backgroundColor: theme.rgba(theme.palette.background[900][0], 0.7),
  borderRadius: '0.5rem 0 0 0.5rem',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  height: '2.5rem',
  ...compactChrome({ fontSize: '0.625rem', marginTop: '0.25rem' }, { position: 'absolute' }),
}));

export const CarrierImageMessage = styled.img({
  width: '1rem',
  height: 'auto',
  margin: 'auto 0 auto 0.5rem',
  ...compactChrome({ width: '0.75rem', height: 'auto', margin: 'auto 0 auto 0.25rem' }),
});

export const WatermarkContainer = styled.div(() => ({
  position: 'fixed',
  right: '1.55rem',
  top: '1rem',
  fontSize: '1rem',
  opacity: 1,
  '& svg': {
    width: '7rem',
  },
  '@media (max-width: 1000px) and (min-width: 701px)': {
    '& svg': {
      width: '6rem',
    },
  },
  '@media (max-width: 700px)': {
    '& svg': {
      width: '5rem',
    },
  },
  // In the shorts panel the player is a box inside the page rather than the viewport, so
  // `fixed` parks the logo over whatever sits at the window's top-right — the info panel on
  // desktop. Anchor it to the player instead.
  '.short-player &': {
    position: 'absolute',
    right: '1rem',
    '& svg': {
      width: '5rem',
    },
  },
}));
export const SelectingItemMainWrapper = styled.div(() => ({
  maxHeight: '23rem',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '&::-webkit-scrollbar-thumb': {
    display: 'none',
  },
  ...compactChrome({ minWidth: '15rem', '@media (max-height: 360px)': { maxHeight: '11.5rem' } }),
}));

export const SettingWrapper = styled.div(() => ({
  minWidth: '18.8rem',
  maxHeight: '23rem',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '&::-webkit-scrollbar-thumb': {
    display: 'none',
  },
  ...compactChrome({ minWidth: '15rem', '@media (max-height: 360px)': { maxHeight: '13.5rem' } }),
}));

export const GradientX = styled.div(({ theme }) => ({
  background: `linear-gradient(to top, ${theme.palette.background[900][0]}, rgba(0,0,0,0))`,
  position: 'absolute',
  bottom: -1,
  width: '100%',
  height: '12%',
  zIndex: 1,
  ...compactChrome({ height: '23%' }),
}));
